import React, { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { Card, Badge, Button, Input, Select } from "./ui";
import { ShieldAlert, Globe, Radar, Ban, Activity, Clock, AlertTriangle, Trash2, Plus, RefreshCw, Usb, Copy, KeyRound } from "lucide-react";

const sev = { high: "danger", medium: "warning", low: "neutral" };

export default function OlosoPanel() {
  const [tab, setTab] = useState("outside");
  const [devicesOn, setDevicesOn] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    client.get("/oloso/device-feature").then((r) => setDevicesOn(!!r.data.enabled)).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  const enableDevices = async () => {
    try { await client.put("/oloso/device-feature", { enabled: true }); setDevicesOn(true); setTab("devices"); } catch { /* noop */ }
  };
  const disableDevices = async () => {
    try { await client.put("/oloso/device-feature", { enabled: false }); setDevicesOn(false); setTab("outside"); } catch { /* noop */ }
  };
  return (
    <Card className="space-y-4" data-testid="oloso-panel">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldAlert size={18} className="text-[var(--primary)]" />
        <span className="font-head text-lg">OLOSO — Onboard Local Operation Security Office</span>
        <div className="ms-auto flex gap-1 rounded-lg bg-black/5 dark:bg-white/10 p-1">
          <button onClick={() => setTab("outside")} data-testid="oloso-tab-outside" className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm ${tab === "outside" ? "bg-[var(--primary)] text-white" : ""}`}><Globe size={14} /> OUTSIDE</button>
          <button onClick={() => setTab("inside")} data-testid="oloso-tab-inside" className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm ${tab === "inside" ? "bg-[var(--primary)] text-white" : ""}`}><Activity size={14} /> INSIDE</button>
          {devicesOn && <button onClick={() => setTab("devices")} data-testid="oloso-tab-devices" className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm ${tab === "devices" ? "bg-[var(--primary)] text-white" : ""}`}><Usb size={14} /> DEVICES</button>}
        </div>
      </div>
      {loaded && !devicesOn && (
        <button onClick={enableDevices} data-testid="oloso-enable-devices"
          className="flex items-center gap-2 rounded-lg border border-dashed border-black/15 dark:border-white/15 px-3 py-1.5 text-xs opacity-70 hover:opacity-100">
          <Usb size={13} /> Enable support-workstation device controls (optional — for managed support seats)
        </button>
      )}
      {tab === "outside" ? <Outside /> : tab === "inside" ? <Inside /> : <Devices onDisable={disableDevices} />}
    </Card>
  );
}

function Devices({ onDisable }) {
  const [pol, setPol] = useState(null);
  const [ev, setEv] = useState({ events: [], total: 0, violations: 0, blocked: 0, by_type: {}, agents: [] });
  const [keyInfo, setKeyInfo] = useState({ set: false });
  const [newKey, setNewKey] = useState("");
  const [comp, setComp] = useState(null);
  const [hb, setHb] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    client.get("/oloso/device-policy").then((r) => setPol(r.data)).catch(() => {});
    client.get("/oloso/device-events").then((r) => setEv(r.data)).catch(() => {});
    client.get("/oloso/agent-key").then((r) => setKeyInfo(r.data)).catch(() => {});
    client.get("/oloso/agents").then((r) => setComp(r.data)).catch(() => {});
    client.get("/oloso/heartbeat-config").then((r) => setHb(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  // Live auto-refresh (compliance + events only — leaves the policy matrix you're editing untouched)
  const refreshLive = useCallback(() => {
    client.get("/oloso/agents").then((r) => setComp(r.data)).catch(() => {});
    client.get("/oloso/device-events").then((r) => setEv(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const id = setInterval(refreshLive, 45000);
    return () => clearInterval(id);
  }, [refreshLive]);

  const toggle = (role, key) => setPol((p) => ({ ...p, scopes: { ...p.scopes, [role]: { ...p.scopes[role], [key]: !p.scopes[role][key] } } }));
  const save = async () => {
    try { const { data } = await client.put("/oloso/device-policy", { scopes: pol.scopes }); setPol({ ...pol, ...data }); setMsg("Policy saved."); load(); setTimeout(() => setMsg(""), 2000); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const saveHb = async () => {
    try { const { data } = await client.put("/oloso/heartbeat-config", hb); setHb(data); setMsg("Thresholds saved."); setTimeout(() => setMsg(""), 2000); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const rotate = async () => {
    if (!window.confirm("Generate a new agent key? Any agent using the old key will stop reporting until updated.")) return;
    try { const { data } = await client.post("/oloso/agent-key/rotate"); setNewKey(data.key); load(); } catch (e) { setMsg(apiErr(e)); }
  };
  const repush = async (agentId) => {
    try { await client.post(`/oloso/agents/${agentId}/repush`); setMsg("Re-push queued — applies on the seat's next heartbeat."); load(); setTimeout(() => setMsg(""), 2500); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const repushAll = async () => {
    if (!window.confirm("Queue a policy re-push to ALL non-compliant seats?")) return;
    try { const { data } = await client.post("/oloso/agents/repush-all"); setMsg(`Re-push queued for ${data.queued} seat(s).`); load(); setTimeout(() => setMsg(""), 2800); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const stBadge = { offline: ["danger", "offline"], drift: ["danger", "policy drift"], out_of_date: ["warning", "out-of-date"], ok: ["success", "compliant"] };

  return (
    <div className="space-y-4" data-testid="oloso-devices">
      <div className="rounded-lg border border-[#F1C21B]/50 bg-[#F1C21B]/10 px-3 py-2 text-xs" data-testid="oloso-devices-note">
        <b>Support-workstation device control.</b> This governs your <b>customer-support seats</b> and any managed / company-issued devices — <b>not paying customers (runners)</b>, who are unrestricted by default. Violation and agent-compliance alerts are raised for support seats only.
        <br />The Master Enabler is the <b>policy &amp; alerting console</b>: a thin-client / endpoint agent (or your MDM/VDI) must be installed to actually block ports/drives and report events — a hosted web app cannot control hardware on its own.
        {onDisable && <><br /><button onClick={onDisable} data-testid="oloso-disable-devices" className="mt-1 underline opacity-70 hover:opacity-100">Turn off support-workstation controls</button></>}
      </div>

      <div className="grid grid-cols-3 gap-2 text-center text-sm" data-testid="oloso-device-stats">
        <div className="rounded-lg bg-black/5 p-2 dark:bg-white/10"><div className="text-lg font-bold">{ev.total}</div><div className="opacity-60">events</div></div>
        <div className="rounded-lg bg-[#DA1E28]/10 p-2"><div className="text-lg font-bold text-[#DA1E28]" data-testid="oloso-device-violations">{ev.violations}</div><div className="opacity-60">violations</div></div>
        <div className="rounded-lg bg-black/5 p-2 dark:bg-white/10"><div className="text-lg font-bold">{ev.blocked}</div><div className="opacity-60">blocked</div></div>
      </div>

      {/* Central device policy */}
      <div className="rounded-xl border border-black/10 p-3 dark:border-white/10" data-testid="oloso-device-policy">
        <div className="mb-2 flex items-center gap-2"><Usb size={15} /><b className="text-sm">Support-workstation port &amp; drive policy</b>
          {msg && <span className="text-xs text-[var(--primary)]" data-testid="oloso-policy-msg">{msg}</span>}
          {pol && <Button variant="subtle" className="ms-auto" onClick={save} data-testid="oloso-policy-save">Save policy</Button>}</div>
        {!pol ? <p className="text-xs opacity-60">Loading…</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="oloso-policy-table">
              <thead><tr className="text-start opacity-60"><th className="py-1">Control</th>{pol.roles.map((r) => <th key={r} className="px-2 capitalize">{r === "user" ? "runner" : r}</th>)}</tr></thead>
              <tbody>
                {pol.controls.map((c) => (
                  <tr key={c.key} className="border-t border-black/5 dark:border-white/5">
                    <td className="py-1.5">{c.label}</td>
                    {pol.roles.map((r) => {
                      const allowed = pol.scopes[r][c.key];
                      return (
                        <td key={r} className="px-2 text-center">
                          <button onClick={() => toggle(r, c.key)} data-testid={`oloso-pol-${r}-${c.key}`}
                            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${allowed ? "bg-emerald-500/15 text-emerald-600" : "bg-[#DA1E28]/15 text-[#DA1E28]"}`}>
                            {allowed ? "allowed" : "blocked"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Agent provisioning key */}
      <div className="rounded-xl border border-black/10 p-3 dark:border-white/10" data-testid="oloso-agent-key">
        <div className="mb-1 flex items-center gap-2"><KeyRound size={15} /><b className="text-sm">Endpoint agent key</b>
          <Button variant="ghost" className="ms-auto" onClick={rotate} data-testid="oloso-key-rotate"><RefreshCw size={13} /> {keyInfo.set ? "Rotate key" : "Generate key"}</Button></div>
        <p className="text-xs opacity-60">Agents authenticate ingestion &amp; policy-fetch with header <code>X-OLOSO-Agent-Key</code>. {keyInfo.set ? `Current key ends in …${keyInfo.hint}.` : "No key set yet."}</p>
        {newKey && (
          <div className="mt-2 flex items-center gap-2 rounded-md bg-black/5 p-2 text-xs dark:bg-white/10" data-testid="oloso-key-new">
            <code className="break-all">{newKey}</code>
            <button onClick={() => { navigator.clipboard?.writeText(newKey); }} className="opacity-70 hover:opacity-100"><Copy size={13} /></button>
            <span className="opacity-60">Copy now — shown once.</span>
          </div>
        )}
      </div>

      {/* Seat compliance heartbeat */}
      <div className="rounded-xl border border-black/10 p-3 dark:border-white/10" data-testid="oloso-compliance">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
          <RefreshCw size={15} /><b>Seat compliance</b>
          {comp && <span className="opacity-60">policy v{comp.current_version}</span>}
          <span className="flex items-center gap-1 text-[11px] text-emerald-600" data-testid="oloso-compliance-live" title="Auto-refreshes every 45s">
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> live</span>
          {comp && (comp.summary.total - comp.summary.ok) > 0 && (
            <button onClick={repushAll} data-testid="oloso-repush-all"
              className="rounded-md border border-black/15 px-2 py-0.5 text-[11px] lift hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
              <RefreshCw size={11} className="inline" /> Re-push to all non-compliant
            </button>
          )}
          {hb && (
            <span className="ms-auto flex items-center gap-2 text-xs">
              <label className="flex items-center gap-1">offline after
                <input type="number" value={hb.offline_minutes} onChange={(e) => setHb({ ...hb, offline_minutes: parseInt(e.target.value) || 0 })}
                  data-testid="oloso-hb-offline" className="w-14 rounded border border-black/15 bg-transparent px-1 py-0.5 dark:border-white/15" /> min</label>
              <label className="flex items-center gap-1">warn ≥
                <input type="number" value={hb.warn_versions_behind} onChange={(e) => setHb({ ...hb, warn_versions_behind: parseInt(e.target.value) || 1 })}
                  data-testid="oloso-hb-behind" className="w-12 rounded border border-black/15 bg-transparent px-1 py-0.5 dark:border-white/15" /> ver behind</label>
              <Button variant="subtle" onClick={saveHb} data-testid="oloso-hb-save">Save</Button>
            </span>
          )}
        </div>
        {comp && (
          <div className="mb-2 flex flex-wrap gap-2 text-xs" data-testid="oloso-compliance-summary">
            <span className="rounded-full bg-black/5 px-2 py-0.5 dark:bg-white/10">{comp.summary.total} seats</span>
            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-600">{comp.summary.online} online</span>
            <span className="rounded-full bg-[#DA1E28]/15 px-2 py-0.5 text-[#DA1E28]">{comp.summary.offline} offline</span>
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-600">{comp.summary.out_of_date} out-of-date</span>
            <span className="rounded-full bg-[#DA1E28]/15 px-2 py-0.5 text-[#DA1E28]">{comp.summary.drift} drift</span>
          </div>
        )}
        {(!comp || comp.agents.length === 0) ? (
          <p className="text-xs opacity-60">No agents have reported in yet. Once the endpoint/thin-client agent is deployed with the key above, each seat appears here with live compliance status.</p>
        ) : (
          <table className="w-full text-xs" data-testid="oloso-compliance-table">
            <thead><tr className="text-start opacity-60"><th className="py-1">Seat</th><th>Role</th><th>Policy</th><th>Last seen</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {comp.agents.map((a) => {
                const b = stBadge[a.status] || ["neutral", a.status];
                const needsFix = a.status !== "ok";
                return (
                  <tr key={a.agent_id} className="border-t border-black/5 dark:border-white/5" data-testid={`oloso-seat-${a.agent_id}`}>
                    <td className="py-1.5">{a.agent_label}<span className="opacity-50"> · {a.host || "—"}</span></td>
                    <td className="capitalize">{a.role === "user" ? "runner" : a.role}</td>
                    <td>v{a.policy_version}/{a.current_version}{a.drift_keys.length > 0 && <span className="text-[#DA1E28]"> · drift: {a.drift_keys.join(", ")}</span>}</td>
                    <td>{(a.last_seen || "").slice(5, 16).replace("T", " ")}</td>
                    <td><Badge color={b[0]}>{b[1]}</Badge></td>
                    <td className="text-end">
                      {a.repush_requested
                        ? <span className="text-[11px] text-amber-600" data-testid={`oloso-repush-pending-${a.agent_id}`}>re-push queued…</span>
                        : (needsFix && <button onClick={() => repush(a.agent_id)} data-testid={`oloso-repush-${a.agent_id}`}
                            className="rounded-md border border-black/15 px-2 py-0.5 text-[11px] lift hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10"><RefreshCw size={11} className="inline" /> Re-push policy</button>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent device events */}
      <div>
        <div className="mb-1 flex items-center gap-2 text-sm"><b>Recent device events</b>
          <button onClick={load} className="opacity-60 hover:opacity-100" data-testid="oloso-devices-refresh"><RefreshCw size={13} /></button></div>
        {ev.events.length === 0 ? <p className="text-xs opacity-60">No device events reported yet (install an endpoint agent to begin).</p> : (
          <table className="w-full text-xs" data-testid="oloso-device-log">
            <thead><tr className="text-start opacity-60"><th className="py-1">Time</th><th>Agent</th><th>Event</th><th>Device</th><th>Action</th></tr></thead>
            <tbody>
              {ev.events.map((e) => (
                <tr key={e.id} className={`border-t border-black/5 dark:border-white/5 ${e.violation ? "bg-[#DA1E28]/5" : ""}`} data-testid={`oloso-device-ev-${e.id}`}>
                  <td className="py-1">{(e.at || "").slice(5, 16).replace("T", " ")}</td>
                  <td>{e.agent_label}</td>
                  <td>{e.event_type}</td>
                  <td>{e.device_desc || e.device_kind || "—"}</td>
                  <td>{e.violation ? <Badge color="danger">{e.action}</Badge> : <Badge>{e.action}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function Outside() {
  const [data, setData] = useState({ scans: [], total: 0, blocked: 0, clear: 0, contact_total: 0, contact_blocked: 0, contact_clean: 0 });
  const [bl, setBl] = useState([]);
  const [entry, setEntry] = useState({ kind: "ip", value: "" });
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    client.get("/oloso/scans").then((r) => setData(r.data)).catch((e) => setMsg(apiErr(e)));
    client.get("/oloso/blocklist").then((r) => setBl(r.data.entries)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const addBlock = async () => { if (!entry.value.trim()) return; try { await client.post("/oloso/blocklist", entry); setEntry({ kind: "ip", value: "" }); load(); } catch (e) { setMsg(apiErr(e)); } };
  const delBlock = async (id) => { try { await client.delete(`/oloso/blocklist/${id}`); load(); } catch (e) { setMsg(apiErr(e)); } };

  return (
    <div className="space-y-4" data-testid="oloso-outside">
      <p className="text-sm opacity-70">Every attempted connection to the Master Enabler is pre-scanned. Connectors see a scan disclosure; flagged devices are rejected. Full threat detail is shown here (Admin only).</p>
      <div className="grid grid-cols-3 gap-3">
        {[["Scanned", data.total], ["Blocked", data.blocked], ["Clear", data.clear]].map(([l, v]) => (
          <div key={l} className="rounded-xl glass p-4"><div className="font-head text-2xl">{v}</div><div className="text-xs opacity-60">{l}</div></div>
        ))}
      </div>

      <div className="rounded-xl border border-black/10 dark:border-white/10 p-3" data-testid="oloso-contact-abuse">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium"><ShieldAlert size={15} className="text-[var(--primary)]" /> Contact-form guard</div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><div className="font-head text-xl">{data.contact_total}</div><div className="text-[11px] opacity-60">Submissions scanned</div></div>
          <div><div className="font-head text-xl text-[#DA1E28]">{data.contact_blocked}</div><div className="text-[11px] opacity-60">Bot / spam blocked</div></div>
          <div><div className="font-head text-xl text-emerald-600">{data.contact_clean}</div><div className="text-[11px] opacity-60">Legitimate</div></div>
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-black/15 dark:border-white/15 p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium"><Ban size={15} /> Blocklist</div>
        <div className="flex flex-wrap gap-2">
          <Select value={entry.kind} onChange={(e) => setEntry({ ...entry, kind: e.target.value })} data-testid="oloso-block-kind" className="w-28"><option value="ip">IP</option><option value="ua">User-agent</option></Select>
          <Input value={entry.value} onChange={(e) => setEntry({ ...entry, value: e.target.value })} placeholder={entry.kind === "ip" ? "e.g. 203.0.113.9" : "e.g. sqlmap"} data-testid="oloso-block-value" className="flex-1 min-w-[180px]" />
          <Button onClick={addBlock} data-testid="oloso-block-add"><Plus size={15} /> Block</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {bl.map((e) => <span key={e.id} className="flex items-center gap-1 rounded-full bg-black/5 dark:bg-white/10 px-2.5 py-1 text-xs" data-testid={`oloso-block-${e.id}`}>{e.kind}:{e.value}<button onClick={() => delBlock(e.id)} className="opacity-60 hover:text-[#DA1E28]"><Trash2 size={12} /></button></span>)}
          {bl.length === 0 && <span className="text-xs opacity-50">No blocklist entries.</span>}
        </div>
      </div>

      {msg && <p className="text-sm text-[#DA1E28]">{msg}</p>}
      <div className="flex items-center gap-2"><Radar size={15} className="text-[var(--primary)]" /><span className="text-sm font-medium">Connection scan log</span><button onClick={load} className="ms-auto opacity-60 hover:opacity-100"><RefreshCw size={14} /></button></div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="oloso-scan-log">
          <thead><tr className="text-start opacity-60"><th className="py-1">When (UTC)</th><th>Verdict</th><th>IP</th><th>Threats found</th><th>User-agent</th></tr></thead>
          <tbody>
            {data.scans.map((s) => (
              <tr key={s.id} className="border-t border-black/5 dark:border-white/5" data-testid={`oloso-scan-${s.id}`}>
                <td className="py-1.5">{(s.at || "").slice(0, 16).replace("T", " ")}</td>
                <td><Badge color={s.verdict === "blocked" ? "danger" : "success"}>{s.verdict}</Badge></td>
                <td className="font-mono">{s.ip}</td>
                <td>{(s.threats || []).map((t, i) => <div key={i}><b>{t.category}</b> — {t.detail}</div>) || "—"}{(s.threats || []).length === 0 && "—"}</td>
                <td className="max-w-[220px] truncate opacity-70">{s.user_agent}</td>
              </tr>
            ))}
            {data.scans.length === 0 && <tr><td colSpan={5} className="py-2 opacity-60">No connection scans recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Inside() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [audit, setAudit] = useState(null);
  const [live, setLive] = useState({ active_agents: [], open_profiles: [] });
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    client.get(`/oloso/inside?date=${date}`).then((r) => { setAudit(r.data); setCfg(r.data.config); }).catch((e) => setMsg(apiErr(e)));
    client.get("/oloso/live").then((r) => setLive(r.data)).catch(() => {});
  }, [date]);
  useEffect(() => { load(); }, [load]);
  const saveCfg = async () => { try { const { data } = await client.put("/oloso/config", cfg); setCfg(data); load(); } catch (e) { setMsg(apiErr(e)); } };

  return (
    <div className="space-y-4" data-testid="oloso-inside">
      {/* live */}
      <div className="rounded-xl border border-black/10 dark:border-white/10 p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium"><Activity size={15} className="text-emerald-500" /> Real-time — {live.active_agents.length} active agent(s), {live.open_profiles.length} open profile(s)</div>
        <div className="grid gap-3 sm:grid-cols-2 text-xs">
          <div data-testid="oloso-live-agents">{live.active_agents.map((a, i) => <div key={i} className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {a.agent} <span className="opacity-50">· seen {(a.last_seen || "").slice(11, 16)}</span></div>)}{live.active_agents.length === 0 && <span className="opacity-50">No agents online.</span>}</div>
          <div data-testid="oloso-live-open">{live.open_profiles.map((p, i) => <div key={i} className="flex items-center gap-2"><Clock size={12} className="text-amber-500" /> {p.agent} in “{p.client}” <span className="opacity-50">since {(p.opened_at || "").slice(11, 16)}</span></div>)}{live.open_profiles.length === 0 && <span className="opacity-50">No profiles open.</span>}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="oloso-inside-date" className="w-44" />
        <button onClick={load} className="opacity-60 hover:opacity-100"><RefreshCw size={15} /></button>
        {cfg && (
          <div className="ms-auto flex flex-wrap items-center gap-2 text-xs">
            <label className="flex items-center gap-1">Max visit min <input type="number" value={cfg.max_visit_minutes} onChange={(e) => setCfg({ ...cfg, max_visit_minutes: parseInt(e.target.value) })} data-testid="oloso-cfg-maxvisit" className="w-16 rounded border border-black/15 dark:border-white/15 bg-transparent px-2 py-1" /></label>
            <label className="flex items-center gap-1">Max profiles/hr <input type="number" value={cfg.max_profiles_per_hour} onChange={(e) => setCfg({ ...cfg, max_profiles_per_hour: parseInt(e.target.value) })} data-testid="oloso-cfg-maxprofiles" className="w-16 rounded border border-black/15 dark:border-white/15 bg-transparent px-2 py-1" /></label>
            <Button variant="subtle" onClick={saveCfg} data-testid="oloso-cfg-save">Save presets</Button>
          </div>
        )}
      </div>
      {msg && <p className="text-sm text-[#DA1E28]">{msg}</p>}

      {/* anomalies */}
      {audit && (
        <div data-testid="oloso-anomalies">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium"><AlertTriangle size={15} className="text-amber-500" /> Pattern anomalies ({audit.anomalies.length})</div>
          {audit.anomalies.length === 0 && <p className="text-xs opacity-60">No anomalies detected for {audit.date}.</p>}
          <div className="space-y-1">
            {audit.anomalies.map((a, i) => (
              <div key={i} className="flex items-start gap-2 rounded-md border border-black/5 dark:border-white/5 p-2 text-xs" data-testid={`oloso-anomaly-${i}`}>
                <Badge color={sev[a.severity] || "neutral"}>{a.severity}</Badge><span className="opacity-80">{a.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* per-agent daily audit */}
      {audit && (
        <div className="space-y-3" data-testid="oloso-agents">
          {audit.agents.length === 0 && <p className="text-xs opacity-60">No profile visits recorded on {audit.date}.</p>}
          {audit.agents.map((a) => (
            <div key={a.agent} className="rounded-xl border border-black/10 dark:border-white/10 p-3" data-testid={`oloso-agent-${a.agent}`}>
              <div className="mb-2 text-sm font-medium">{a.agent} <Badge color="info">{a.count} visit(s)</Badge></div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-start opacity-60"><th className="py-1">Profile</th><th>Opened</th><th>Duration</th><th>Owner contacted</th><th>Means</th><th>Notes</th><th>Esc.</th></tr></thead>
                  <tbody>
                    {a.visits.map((v, i) => (
                      <tr key={i} className="border-t border-black/5 dark:border-white/5">
                        <td className="py-1">{v.client}{v.open && <Badge color="warning">open</Badge>}</td>
                        <td>{v.opened_at}</td><td>{v.duration_min ? `${v.duration_min}m` : "—"}</td>
                        <td>{v.owner_contacted_today ? <Badge color="success">yes</Badge> : <span className="opacity-50">no</span>}</td>
                        <td>{v.contact_means || "—"}</td><td>{v.notes_made}</td><td>{v.escalations_sent}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {audit.cross_reference_names.length > 0 && (
            <div className="text-xs opacity-70" data-testid="oloso-xref">Name cross-reference (same-day contacts): {audit.cross_reference_names.join(", ")}</div>
          )}
        </div>
      )}
    </div>
  );
}
